import React, { useCallback, useEffect, useMemo, useState } from "react";
import classes from "./QualitativeData.module.css";
import "../SentimentWidget/SentimentWidget.css";
import { Select, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import LessThanOnePercentModal from "../NumberMetadataBreakdown/LessThanOnePercentModal";
import MetaFilterModal from "./MetaFilterModal";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { QuantitativeMetadataBarChart } from "../NumberMetadataBreakdown/QuantitativeMetadataBarChart";

const loadingIndicator = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const getMetadataFields = (metadata) =>
    metadata.filter(
        (el) =>
            el.type !== "number" &&
            el.type !== "score" &&
            el.type !== "date",
    );

const getMetadataOptions = (metadataFields) =>
    metadataFields.map((field) => ({
        label: field.name,
        value: field.name,
    }));

export const QualitativeMetadataBreakdown = (props) => {
    const { id, metaDataQu } = props;
    const metadataFields = useMemo(() =>
            getMetadataFields(
                metaDataQu.metaData.filter(value => value.values && value.values.length),
            ),
        [metaDataQu.metaData],
    );
    const metadataOptions = useMemo(() => getMetadataOptions(metadataFields), [metadataFields]);
    const [metadataName, setMetadataName] = useState(metadataFields[0]?.name || null);
    const [chartData, setChartData] = useState([]);
    const [showLessThanOnePercentModal, setShowLessThanOnePercentModal] = useState(false);
    const [lessThanOnePercentValues, setLessThanOnePercentValues] = useState([]);
    const [topConceptsBySentiment, setTopConceptsBySentiment] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showMetaFilterModal, setShowMetaFilterModal] = useState(false);
    const [getVolume] = useGetConceptsFiltersMutation();
    const [size, setSize] = useState(null);
    const selectedMetadataField = metadataFields.find(field => field.name === metadataName);

    useEffect(() => {
        if (selectedMetadataField) {
            const totalCount = selectedMetadataField.values.reduce((sum, item) => sum + item.count, 0);
            let pieData = selectedMetadataField.values.map((item) => ({
                name: item.value,
                value: item.count / totalCount,
                count: item.count,
                concept:item
            }));

            const lessThanThreePercent = pieData.filter(item => item.value < 0.01);
            setLessThanOnePercentValues(lessThanThreePercent);
            pieData = pieData.filter(item => item.value >= 0.01).concat({
                name: "< 1%",
                value: lessThanThreePercent.reduce((acc, item) => acc + item.value, 0),
                count: lessThanThreePercent.reduce((acc, item) => acc + item.count, 0),
            });

            if (pieData.length > 10) {
                const lessThanThreePercent = pieData.filter(item => item.value < 0.03);
                setLessThanOnePercentValues(lessThanThreePercent);
                pieData = pieData.filter(item => item.value >= 0.03).concat({
                    name: "< 3%",
                    value: lessThanThreePercent.reduce((acc, item) => acc + item.value, 0),
                    count: lessThanThreePercent.reduce((acc, item) => acc + item.count, 0),
                });
            }

            setChartData(pieData);
        }
    }, [selectedMetadataField]);

    const fetchFilteredData = useCallback(async (filter) => {
        setIsLoading(true);
        try {
            const { data: dataConcepts } = await getVolume({
                id,
                filter,
            });

            const topConcepts = dataConcepts.match_counts
                .map((concept) => ({
                    name: concept.name,
                    value: concept.exact_match_count,
                    count: concept.exact_match_count,
                    concept:concept
                }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10);

            setTopConceptsBySentiment(topConcepts);
            setShowMetaFilterModal(true);
        } catch (error) {
            console.error("Error fetching filtered data", error);
        } finally {
            setIsLoading(false);
        }
    }, [getVolume, id]);

    const handlePieClick = (entry) => {
        if (entry.name === "< 1%" || entry.name === "< 3%") {
            setSize(entry.name);
            setShowLessThanOnePercentModal(true);
            return;
        }
        setShowLessThanOnePercentModal(false);
        const newFilter = [{ name: metadataName, values: [entry.name] }];
        fetchFilteredData(newFilter);
    };

    const handleMetaFilterModalClose = () => {
        setShowMetaFilterModal(false);
        setTopConceptsBySentiment([]);
    };
    useEffect(() => {
        props.setAdditionalFilters([{
            key: "Special",
            label: "Select Filter:",
            showArrow: false,
            children: <SpecialFilter
                metadataName={metadataName}
                setMetadataName={setMetadataName}
                metadataOptions={metadataOptions}
            />,
        }]);
    }, [metadataName]);

    return (
        <div style={{ height: props.widget.layout.h * 39 - 150 }} >
            {selectedMetadataField && (
                <QuantitativeMetadataBarChart
                    chartData={chartData}
                    handlePieClick={handlePieClick}
                />
            )}
            <LessThanOnePercentModal
                title={size}
                open={showLessThanOnePercentModal}
                values={lessThanOnePercentValues}
                onClickValue={handlePieClick}
                onClose={() => setShowLessThanOnePercentModal(false)}
            />
            {isLoading && <Spin indicator={loadingIndicator} />}
            <MetaFilterModal
                visible={showMetaFilterModal}
                onClose={handleMetaFilterModalClose}
                content={topConceptsBySentiment}
            />
        </div>
    );
};

const SpecialFilter = ({
                           metadataName,
                           setMetadataName,
                           metadataOptions,
                       }) => {
    return (
        <div className={classes.select} style={{ alignItems: "flex-start" }}>
            <Select
                showSearch
                style={{ width: 150 }}
                placeholder="Select Metadata"
                value={metadataName}
                onChange={(value) => setMetadataName(value)}
                options={metadataOptions}
                size="small"
            />
        </div>
    );

};
